import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { downloadBlob } from "../lib/api";
import Topbar from "../components/Topbar";
import EmptyState from "../components/EmptyState";
import PizzaSpinner from "../components/PizzaSpinner";
import { useAuth } from "../lib/auth";
import { Store, Package, Wrench, Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const GROUP_LABEL = { insumos: "Insumos", servicios: "Servicios" };
const GROUP_ICON = { insumos: Package, servicios: Wrench };

export default function SuppliersPendingConfig() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canConfigure = user?.role === "corporate_admin";
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = () => api.get("/suppliers/reports/branch-operations-status").then((r) => setReport(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/suppliers/reports/branch-operations-status/export.xlsx", { responseType: "blob" });
      downloadBlob(r.data, "proveedores_pendientes_configuracion.xlsx");
      toast.success("Archivo descargado");
    } catch {
      toast.error("No se pudo exportar");
    } finally {
      setExporting(false);
    }
  };

  if (!report) return <><Topbar title="Proveedores pendientes de configuración" /><PizzaSpinner /></>;

  const pending = report.list_pending || [];
  const grouped = { insumos: [], servicios: [] };
  pending.forEach((p) => { (grouped[p.supplier_type] || (grouped[p.supplier_type] = [])).push(p); });

  return (
    <>
      <Topbar
        title="Proveedores pendientes de configuración"
        subtitle={`${pending.length} proveedores esperan configuración de operaciones por sucursal`}
      />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Configurados</div>
              <div className="text-xl font-display font-extrabold text-emerald-700 mt-1">{report.with_operations_configured}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Parciales</div>
              <div className="text-xl font-display font-extrabold text-amber-600 mt-1">{report.partially_configured}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Sin configurar</div>
              <div className="text-xl font-display font-extrabold text-red-600 mt-1">{report.pending_setup}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">No aplica (1 sucursal)</div>
              <div className="text-xl font-display font-extrabold text-slate-400 mt-1">{report.single_branch_or_less}</div>
            </div>
          </div>
          <button data-testid="pending-config-export-btn" onClick={exportXlsx} disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold disabled:opacity-50">
            <Download className="w-4 h-4" /> {exporting ? "Exportando..." : "Exportar a Excel"}
          </button>
        </div>

        {pending.length === 0 ? (
          <EmptyState title="¡Todo configurado!" message="No hay proveedores multi-sucursal con operaciones pendientes." />
        ) : (
          ["servicios", "insumos"].map((type) => {
            const items = grouped[type] || [];
            if (items.length === 0) return null;
            const Icon = GROUP_ICON[type];
            return (
              <div key={type} className="space-y-3">
                <div className="flex items-center gap-2 text-[#1A1A1A]">
                  <Icon className="w-4 h-4" />
                  <h2 className="font-display font-bold text-base">{GROUP_LABEL[type]} ({items.length})</h2>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  {items.map((p) => (
                    <div key={p.id} data-testid={`pending-config-row-${p.id}`}
                      className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-100 last:border-0">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-[#1A1A1A]">{p.business_name}</div>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                          <Store className="w-3 h-3" /> {p.operations_configured_count}/{p.branches_authorized_count} sucursales configuradas
                          {p.missing_branches.length > 0 && (
                            <span className="text-amber-700"> · falta: {p.missing_branches.join(", ")}</span>
                          )}
                        </div>
                      </div>
                      <button data-testid={`pending-config-configure-btn-${p.id}`}
                        onClick={() => navigate(`/proveedores/${p.id}?configure_branch_ops=1`)}
                        disabled={!canConfigure}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1A1A1A] hover:bg-black text-white rounded-lg text-xs font-bold flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed">
                        Configurar <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
